/**
 * Schedule wording and next-run maths for the Automations list.
 *
 * The API ships each job's 5-field cron plus a hand-written `scheduleHuman`. The
 * hand-written strings drifted ("Every day at…", "Daily at…", "Every hour, on
 * the hour"), so `scheduleLabel` normalises them; the page header says "Brussels
 * time" once, so the per-row timezone goes.
 */

import { formatDayMonth } from '../../utils/dateFormat';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/** "Every day at 02:00 (Brussels), before the sweep" → "Daily at 02:00, before the sweep". */
export function scheduleLabel(human: string): string {
  let s = human
    // Drop the timezone without leaving a space before the next punctuation mark.
    .replace(/\s*\(Brussels\)/g, '')
    // "(UTC)" on a minute-only schedule ("Every hour at :30") is the same minute in Brussels.
    .replace(/(:\d{2})\s*\(UTC\)/g, '$1')
    .replace(/\s+([,;.])/g, '$1')
    .trim();

  s = s
    .replace(/^Every day\b/i, 'Daily')
    .replace(/^Every weekday\b/i, 'Weekdays')
    .replace(/^Every hour, on the hour$/i, 'Hourly')
    .replace(/^Every hour\b/i, 'Hourly')
    .replace(/^Manual trigger$/i, 'Manual');
  for (const day of DAYS) s = s.replace(new RegExp(`^Every ${day}\\b`, 'i'), `${day}s`);
  return s;
}

/** True when the hand-written schedule says the cron runs in Brussels time; the scheduler's default is UTC. */
function isBrusselsCron(human: string): boolean {
  return /\(Brussels\)/.test(human);
}

function parseField(field: string, min: number, max: number): Set<number> | null {
  if (field === '*') return null; // unrestricted
  const out = new Set<number>();
  for (const part of field.split(',')) {
    const [range, stepStr] = part.split('/');
    const step = stepStr ? Number(stepStr) : 1;
    let lo = min;
    let hi = max;
    if (range && range !== '*') {
      const [a, b] = range.split('-');
      lo = Number(a);
      hi = b !== undefined ? Number(b) : stepStr ? max : lo;
    }
    if (![lo, hi, step].every(Number.isFinite) || step <= 0) return new Set();
    for (let v = lo; v <= hi; v += step) out.add(v);
  }
  return out;
}

/** Minutes east of UTC for a timezone at an instant. */
function tzOffsetMinutes(tz: string, at: Date): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(at);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  const wall = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'));
  return Math.round((wall - Math.floor(at.getTime() / 60_000) * 60_000) / 60_000);
}

/**
 * The next time a 5-field cron fires, as a real instant. Null for anything
 * that isn't a plain cron (manual, webhook) or that won't fire within a year.
 * DST changes between now and the run can shift the answer by an hour; this
 * feeds a "next run" hint, not the scheduler.
 */
export function nextRun(cron: string, human: string, now = new Date()): Date | null {
  const fields = cron.trim().split(/\s+/);
  if (fields.length !== 5) return null;
  const [mF, hF, domF, monF, dowF] = fields as [string, string, string, string, string];
  const minutes = parseField(mF, 0, 59);
  const hours = parseField(hF, 0, 23);
  const doms = parseField(domF, 1, 31);
  const months = parseField(monF, 1, 12);
  const dowsRaw = parseField(dowF, 0, 7);
  const dows = dowsRaw ? new Set([...dowsRaw].map((d) => d % 7)) : null;

  const tz = isBrusselsCron(human) ? 'Europe/Brussels' : 'UTC';
  const offset = tzOffsetMinutes(tz, now);
  // "Wall clock" in the cron's timezone, held in a UTC Date so getUTC* read it directly.
  const wallNow = new Date(now.getTime() + offset * 60_000);
  const minuteList = minutes ? [...minutes].sort((a, b) => a - b) : Array.from({ length: 60 }, (_, i) => i);
  const hourList = hours ? [...hours].sort((a, b) => a - b) : Array.from({ length: 24 }, (_, i) => i);

  for (let d = 0; d < 400; d++) {
    const day = new Date(Date.UTC(wallNow.getUTCFullYear(), wallNow.getUTCMonth(), wallNow.getUTCDate() + d));
    if (months && !months.has(day.getUTCMonth() + 1)) continue;
    const domOk = !doms || doms.has(day.getUTCDate());
    const dowOk = !dows || dows.has(day.getUTCDay());
    // Standard cron: when both day fields are restricted, either one matching is enough.
    if (doms && dows ? !(domOk || dowOk) : !(domOk && dowOk)) continue;
    for (const h of hourList) {
      for (const m of minuteList) {
        const wall = day.getTime() + (h * 60 + m) * 60_000;
        if (wall > wallNow.getTime()) return new Date(wall - offset * 60_000);
      }
    }
  }
  return null;
}

const brusselsTime = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Brussels',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});
const brusselsDay = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Brussels' });
const brusselsWeekday = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Brussels', weekday: 'short' });

/** "06:30", "tomorrow 06:30", "Mon 06:30" or "14 Nov" — in Brussels time, like the rest of the page. */
export function formatNextRun(at: Date, now = new Date()): string {
  const time = brusselsTime.format(at);
  const dayDiff = Math.round(
    (new Date(brusselsDay.format(at)).getTime() - new Date(brusselsDay.format(now)).getTime()) / 86_400_000
  );
  if (dayDiff <= 0) return time;
  if (dayDiff === 1) return `tomorrow ${time}`;
  if (dayDiff < 7) return `${brusselsWeekday.format(at)} ${time}`;
  return formatDayMonth(at);
}

/**
 * The scheduled slot a run was due at: the last time the cron fired at or
 * before `at`, within the hour before it (a queued row is written a moment
 * after its slot). Null when the cron did not fire in that hour, or is not a
 * plain cron.
 */
export function scheduledSlot(cron: string, human: string, at: Date): Date | null {
  let cursor = new Date(at.getTime() - 60 * 60_000 - 1);
  let slot: Date | null = null;
  for (let i = 0; i < 61; i++) {
    const next = nextRun(cron, human, cursor);
    if (!next || next.getTime() > at.getTime()) break;
    slot = next;
    cursor = next;
  }
  return slot;
}

/**
 * The cron slot a run belongs to: the last time the cron fired at or before
 * the run began, looking back up to `lookbackMs` (a day and an hour, so a
 * re-run at 10:19 belongs to that morning's 08:15 slot). Null for a schedule
 * that is not a plain cron or did not fire in that window.
 */
export function slotOfRun(cron: string, human: string, at: Date, lookbackMs = 25 * 60 * 60_000): Date | null {
  let cursor = new Date(at.getTime() - lookbackMs);
  let slot: Date | null = null;
  for (let i = 0; i < 200; i++) {
    const next = nextRun(cron, human, cursor);
    if (!next || next.getTime() > at.getTime()) break;
    slot = next;
    cursor = next;
  }
  return slot;
}
